/**
 * styleEditorial.js — Tokens de design éditoriaux partagés
 *
 * Direction : "Cabinet de famille moderne" — éditorial finance haut de gamme.
 *   Serif Fraunces (display) + Inter Tight (body) + JetBrains Mono (chiffres)
 *   Palette encre + papier crème + or sourd
 *   Bordures nettes, aucune ombre creuse, aucun émoji
 *
 * Utilisé par : AdsLanding, TrustBar, App.jsx (home), Merci, futurs.
 */

// ─── Palette éditoriale ──────────────────────────────────────────────────
export const T = {
  ink:        '#0A1929',  // Texte principal, presque noir bleuté
  inkSoft:    '#3A4A5C',  // Texte secondaire
  // 4,22:1 sur le papier #F4EFE4 avant l'audit 2026-08-15 → sous le minimum WCAG AA
  // de 4,5:1, et ce token sert justement au PETIT texte (captions, eyebrows, mentions).
  // #5F6675 monte à 5,03:1 sur papier (5,43 sur #FAF8F3, 5,76 sur blanc) en restant
  // un gris discret.
  muted:      '#5F6675',  // Captions, eyebrows, mentions
  paper:      '#FAF8F3',  // Background principal, crème léger
  paperWarm:  '#F4EFE4',  // Crème plus saturé pour sections alternées
  paperWhite: '#FFFFFF',  // Cards et formulaire
  rule:       '#0A1929',  // Filets noirs (séparateurs forts)
  ruleLight:  '#D8D3C5',  // Filets fins sur fond papier
  gold:       '#A87A1C',  // Or sourd — wealth management discret (gros titres/accents ≥24px UNIQUEMENT : 3,6:1 sur papier)
  goldSoft:   '#E8D9B0',  // Or pâle pour highlights
  goldHover:  '#8B6314',  // Or plus foncé pour hover
  goldDeep:   '#8B6314',  // Or foncé pour PETITS textes or (<24px) : 5,1:1 sur papier = AA
  marine:     '#0A1929',  // Bleu marine profond (legacy COLORS.dark)
  sage:       '#3F5D52',  // Vert sauge profond pour success
  danger:     '#8B2C1C',  // Rouge brique éditorial (pas SaaS)
};

// ─── Tokens typographiques ───────────────────────────────────────────────
// Direction sans-serif moderne : Inter Tight partout, contraste par les weights
//   300 (light)  → display / h1 / h2 sobres
//   400-500      → body
//   600-700      → emphasis / accents / CTAs
// JetBrains Mono → chiffres, codes, eyebrows
// Chaque pile place JUSTE APRÈS la police web une famille de repli « …Fallback »,
// définie dans public/index.html : c'est une police système (Arial / Times New Roman /
// Courier New) dont les métriques sont recalées — size-adjust, ascent-override,
// descent-override — pour occuper EXACTEMENT la place de la police web.
// Sans ça, le basculement repli → police web recalcule les retours à la ligne et
// décale toute la page : c'était l'origine du CLS de 0,157 (audit 2026-08-15).
// Les valeurs ont été mesurées au canvas sur les vraies polices ; les recalculer
// si l'on change de famille (méthode dans AUDIT-PERF-SEO-2026-08-15.md).
export const FONT_DISPLAY = "'Inter Tight', 'Inter Tight Fallback', -apple-system, BlinkMacSystemFont, sans-serif";
export const FONT_BODY    = "'Inter Tight', 'Inter Tight Fallback', -apple-system, BlinkMacSystemFont, sans-serif";
export const FONT_MONO    = "'JetBrains Mono', 'JetBrains Mono Fallback', 'SF Mono', Consolas, monospace";
// Serif éditoriale pour les GRANDS titres (h1/h2) — personnalité « maison de gestion ».
// Fraunces est auto-hébergée et préchargée par public/index.html.
export const FONT_SERIF   = "'Fraunces', 'Fraunces Fallback', Georgia, 'Times New Roman', serif";

// ─── Styles helper ───────────────────────────────────────────────────────
export const eyebrow = {
  fontFamily: FONT_BODY,
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  color: T.muted,
};

export const eyebrowGold = {
  ...eyebrow,
  color: T.gold,
};

export const ruleStyle = (color = T.rule) => ({
  height: '1px',
  background: color,
  border: 0,
  margin: 0,
});

// Liste de chiffres romains pour numérotation de sections
export const ROMAN = ['I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI', 'XII'];

// Format des numéros à 2 chiffres : "01", "02"…
export const pad2 = (n) => String(n).padStart(2, '0');

// Style de base pour les boutons éditoriaux (à étendre)
export const btnEditorial = (variant = 'primary') => {
  const base = {
    fontFamily: FONT_BODY,
    fontSize: '13px',
    fontWeight: 600,
    letterSpacing: '0.08em',
    textTransform: 'uppercase',
    padding: '15px 32px',
    cursor: 'pointer',
    border: `1px solid ${T.ink}`,
    transition: 'background 0.25s ease, color 0.25s ease, border-color 0.25s ease',
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '10px',
  };

  if (variant === 'primary') {
    return { ...base, background: T.ink, color: T.paper };
  }
  if (variant === 'inverse') {
    return { ...base, background: T.paper, color: T.ink };
  }
  if (variant === 'ghost') {
    return { ...base, background: 'transparent', color: T.ink };
  }
  if (variant === 'gold') {
    return { ...base, background: T.gold, color: T.paper, borderColor: T.gold };
  }
  return base;
};

// Style des inputs underline (style fiche notariale)
export const underlineInput = (active = false) => ({
  width: '100%',
  padding: '14px 2px 10px',
  border: 'none',
  borderBottom: `1px solid ${active ? T.ink : '#B8B0A2'}`,
  background: 'transparent',
  fontFamily: FONT_BODY,
  fontSize: '17px',
  fontWeight: 400,
  color: T.ink,
  outline: 'none',
  boxSizing: 'border-box',
  transition: 'border-color 0.2s ease',
});
